import { addEdge, applyEdgeChanges, applyNodeChanges, Background, MarkerType, ReactFlow, useEdges, useEdgesState, useNodes, useNodesState, useReactFlow } from '@xyflow/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import ApplicationNode from './nodecomponents/ApplicationNode';
import { useDispatch, useSelector } from 'react-redux';
import PolicyProperties from './PolicyProperties';
import { clearSelectedNodeRedux, getSelectedNodeRedux, setSelectedNodeRedux } from '../../../../redux/selectedNodeSlice';
import RestfulApiNode from './nodecomponents/RestfulApiNode';
import useAnimatedNodes from '../../../../utils/useAnimatedNodes';
import useExpandCollapse from '../../../../utils/useExpandCollapse';
import ChildNode from './nodecomponents/ChildNode';
import RouterNode from './nodecomponents/RouterNode';
import PathNode from './nodecomponents/PathNode';
import ChildPathNode from './nodecomponents/ChildPathNode';
import DataNode from './nodecomponents/DataNode';
import { setSnackbarMessage } from '../../../../redux/snackbarMessageSlice';
import ConfirmDialogReuse from '../../ReuseComponents/ConfirmDialogReuse';
import findChildrenNode from '../../../../utils/findChildrenNode';
import { convertPoliciesStructure } from '../../../../utils/convertPolicyStructure';
import findUsedDRGInPolicy from '../../../../utils/findUsedDRGInPolicy';
import findUsedSubjectAttributesInPolicy from '../../../../utils/findUsedSubjectAttributesInPolicy';
import findUsedClients from '../../../../utils/findUsedClients';
import checkDiagramError from '../../../../utils/checkDiagramError';
import checkDRGValidationFrontend from '../../../../utils/checkDRGValidationFrontend';
import { gzip } from 'pako';
import { getSubjectSchema } from '../../../../redux/subjectSchemaSlice';
import checkUpdateSubjectSchema from '../../../../utils/checkUpdateSubjectSchema';
import { dataTypes } from '../../../../utils/configDataTypes';
import { createLDAPValidator } from '../../../../utils/ldapTypeValidator';
import { getClients, setClients } from '../../../../redux/clientsSlice';
import DialogReuse from '../../ReuseComponents/DialogReuse';
import { LoadingButton } from '@mui/lab';
import { handleGetSimulatorProfiles } from '../../../../utils/handleGetSimulatorProfiles';
import { getOpenSimulatorPolicy, setOpenDrawerRight, setOpenSimulatorPolicy } from '../../../../redux/layoutControlSlice';
import { setDRGFiles } from '../../../../redux/drgMgtSlice';
import { DIAGRAM_TOOLBAR_SAVE_EVENT, DIAGRAM_TYPES } from '../../../../constants/diagramToolbarEvents';

const treeWidth = 200;
const treeHeight = 100;
const animationDuration = 300;
const edgeStyle = {
    stroke: process.env.REACT_APP_EDGES_COLOR,
    strokeWidth: 1
};

const edgeHoverColor = process.env.REACT_APP_EDGES_HOVER_COLOR || '#1976d2';
const getEdgeColorByState = ({ isSelected, isHovered }) => ((isSelected || isHovered) ? edgeHoverColor : process.env.REACT_APP_EDGES_COLOR);
const POLICY_FILE_CHANGED_EVENT = 'policy-file-changed';
const POLICY_FILE_UPDATE_STATUS_KEY = 'policy-file-update-status';

const applyEdgeSelectionStyle = (edge, isSelected = edge.selected, isHovered = false) => {
    const color = getEdgeColorByState({
        isSelected: Boolean(isSelected),
        isHovered: Boolean(isHovered),
    });

    return {
        ...edge,
        selected: Boolean(isSelected),
        style: {
            ...(edge.style || {}),
            stroke: color,
            strokeWidth: 1,
        },
        markerEnd: edge.markerEnd
            ? {
                ...edge.markerEnd,
                color,
            }
            : edge.markerEnd,
    };
};

const PolicyDiagramNodeTypeContext = React.createContext({
    onTypeChange: () => { },
    isInteractionDisabled: false,
});

function ChildNodeType(props) {
    const { onTypeChange, isInteractionDisabled } = useContext(PolicyDiagramNodeTypeContext);
    return <ChildNode {...props} onTypeChange={onTypeChange} isInteractionDisabled={isInteractionDisabled} />;
}

function ChildPathNodeType(props) {
    const { onTypeChange, isInteractionDisabled } = useContext(PolicyDiagramNodeTypeContext);
    return <ChildPathNode {...props} onTypeChange={onTypeChange} isInteractionDisabled={isInteractionDisabled} />;
}

const nodeTypes = {
    application: ApplicationNode,
    restfulapi: RestfulApiNode,
    data: DataNode,
    child: ChildNodeType,
    childpath: ChildPathNodeType,
    router: RouterNode,
    path: PathNode
};

const getOperatorValues = (operator) => {
    if (!operator) return [];

    if (typeof operator === 'string') {
        return [operator];
    }

    if (typeof operator === 'object') {
        return [operator.label, operator.condition, operator.constraint].filter(Boolean);
    }

    return [];
};

const isOperatorSupported = (operators = [], operator) => {
    const selectedOperatorValues = getOperatorValues(operator);
    if (selectedOperatorValues.length === 0) {
        return false;
    }

    return operators.some((item) => {
        const availableOperatorValues = getOperatorValues(item);
        return availableOperatorValues.some((value) => selectedOperatorValues.includes(value));
    });
};

const isInOperator = (operator) => {
    const operatorValues = getOperatorValues(operator);
    const normalizedValues = operatorValues.map(v => String(v).toUpperCase());
    return normalizedValues.includes('IN') || normalizedValues.includes('NOT IN');
};

function PolicyDiagram(props) {
    const {
        project,
        profile,
        policyFile,
        setPolicyFile,
        nodes,
        setNodes,
        edges,
        setEdges,
        // handleNodesChange,
        // onEdgesChange
    } = props;

    const selectedNode = useSelector(getSelectedNodeRedux);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const clients = useSelector(getClients);

    const dispatch = useDispatch();

    const {
        zoomTo,
        getViewport,
        getNodes,
        getEdges,
        setViewport,
        fitView,
        screenToFlowPosition,
        getNode,
        addEdges,
        addNodes,
    } = useReactFlow();

    // const [nodes, setNodes, onNodesChangeState] = useNodesState([]);
    // const [edges, setEdges, onEdgesChangeState] = useEdgesState([]);

    // const [nodes, setNodes] = useState([]);
    // const [edges, setEdges] = useState([]);

    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    const hasHandledOpenSimulatorRef = useRef(false);
    const handleSaveRef = useRef(null);

    // useEffect(() => {
    //     setStoreNodes(nodes);
    // }, [nodes, setStoreNodes]);

    // useEffect(() => {
    //     setStoreEdges(edges);
    // }, [edges, setStoreEdges]);

    // const [project, setProject] = useState();
    // const [profile, setProfile] = useState();

    const [isUpdatePolicyFile, setIsUpdatePolicyFile] = useState(false);
    const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

    const [openConfirmRemove, setOpenConfirmRemove] = useState(false);

    const subjectSchema = useSelector(getSubjectSchema);

    const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
    const [clientsInvalid, setClientsInvalid] = useState([]);

    const [flowDelete, setFlowDelete] = useState({
        nodes: [],
        edges: []
    });

    // useEffect(() => {
    //     const project = JSON.parse(localStorage.getItem('project'));
    //     if (project) {
    //         setProject(project);
    //     }
    //     const profile = JSON.parse(localStorage.getItem('iaam'));
    //     if (profile) {
    //         setProfile(profile);
    //     }
    //     const file = JSON.parse(localStorage.getItem('policy-file'));
    //     if (file) {
    //         console.log("Loaded policy file from localStorage:", file);
    //         setPolicyFile(file);
    //     }
    // }, []);

    useEffect(() => {
        nodesRef.current = nodes;
        edgesRef.current = edges;
    }, [nodes, edges]);

    useEffect(() => {
        const getPolicyUpdated = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/policy-diagram/get-policy/${policyFile.policy_diagram_id}`);
                const result = await response.json();
                // console.log("get updated policy file: ", result);
                localStorage.setItem('policy-file', JSON.stringify(result));
                setPolicyFile((prevFile) => {
                    if (
                        prevFile?.policy_diagram_id === result?.policy_diagram_id
                        && prevFile?.last_updated === result?.last_updated
                    ) {
                        return prevFile;
                    }
                    return result;
                })
            } finally {
                localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'idle');
                window.dispatchEvent(new CustomEvent(POLICY_FILE_CHANGED_EVENT));
            }
        };
        if (isUpdatePolicyFile) {
            getPolicyUpdated();
            setIsUpdatePolicyFile(false);
        }
    }, [isUpdatePolicyFile])

    const handleTypeChange = useCallback((id, newType, positionAbsoluteX, positionAbsoluteY) => {
        let nodeChanged = {};
        const parentNode = findParentNode(edgesRef.current, id);
        // console.log("parentNode: ", parentNode);

        let new_subject_constraint = [];
        const subjectConstraint = parentNode?.data?.general_properties?.subjectConstraints;
        if (subjectConstraint.length > 0) {
            new_subject_constraint = subjectConstraint.map(sub => {
                return {
                    ...sub,
                    inherited: true
                }
            })
        }

        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === id) {
                    const { deletable, ...restNode } = n;
                    const { label, ...restData } = n.data;
                    if (newType === 'router') {
                        dispatch(setOpenDrawerRight(true));
                        nodeChanged = {
                            ...restNode,
                            type: newType,
                            data: {
                                ...restData,
                                general_properties: {
                                    label: '',
                                    description: '',
                                    // subjectConstraints: [],
                                    subjectConstraints: new_subject_constraint
                                },
                                error_text: ["General properties incomplete. Please complete them."],
                                restfulapi_parent_node: parentNode.id,
                                environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                            }
                        }
                    } else if (newType === 'path') {
                        dispatch(setOpenDrawerRight(true));
                        if (parentNode.type === 'router') {
                            nodeChanged = {
                                ...restNode,
                                type: newType,
                                data: {
                                    ...restData,
                                    general_properties: {
                                        label: '',
                                        description: '',
                                        // subjectConstraints: [],
                                        subjectConstraints: new_subject_constraint
                                    },
                                    error_text: ["General properties incomplete. Please complete them."],
                                    restfulapi_parent_node: parentNode.data.restfulapi_parent_node,
                                    router_parent_node: parentNode.id
                                }
                            }
                        } else {
                            nodeChanged = {
                                ...restNode,
                                type: newType,
                                data: {
                                    ...restData,
                                    general_properties: {
                                        label: '',
                                        description: '',
                                        // subjectConstraints: [],
                                        subjectConstraints: new_subject_constraint
                                    },
                                    error_text: ["General properties incomplete. Please complete them."],
                                    restfulapi_parent_node: parentNode.id,
                                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                                }
                            }
                        }
                    }
                    return nodeChanged;
                }
                return n;
            })
        );

        if (newType === 'router') {
            const newPathNodeId = uuidv4();
            const newChildNodeId = uuidv4();
            const newPathNode = {
                id: newPathNodeId,
                position: { x: nodeChanged.position.x + 220, y: nodeChanged.position.y - 12 },
                data: {
                    general_properties: {
                        label: '',
                        description: '',
                        // subjectConstraints: [],
                        subjectConstraints: new_subject_constraint,
                        action: null
                    },
                    error_text: ["General properties incomplete. Please complete them."],
                    restfulapi_parent_node: parentNode.id,
                    router_parent_node: id,
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: 'path',
                sourcePosition: 'right',
                targetPosition: 'left',
            };
            const newChildPathNode = {
                id: newChildNodeId,
                position: { x: nodeChanged.position.x + 220, y: nodeChanged.position.y + 50 },
                data: {
                    general_properties: {
                        label: '',
                        description: '',
                        // subjectConstraints: [],
                        subjectConstraints: new_subject_constraint,
                    },
                    error_text: [],
                    restfulapi_parent_node: parentNode.id,
                    router_parent_node: id,
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: 'childpath',
                sourcePosition: 'right',
                targetPosition: 'left',
                // deletable: false
            };
            addNodes([newPathNode, newChildPathNode]);

            const newPathEdge = {
                id: `${nodeChanged.id}->${newPathNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: process.env.REACT_APP_EDGES_COLOR
                },
                source: nodeChanged.id,
                target: newPathNodeId,
            };
            const newChildPathEdge = {
                id: `${nodeChanged.id}->${newChildNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: process.env.REACT_APP_EDGES_COLOR
                },
                source: nodeChanged.id,
                target: newChildNodeId,
            };
            addEdges([newPathEdge, newChildPathEdge]);

        }
        const newNodeId = uuidv4();
        // const parentNode = findParentNode(edgesRef.current, id);

        if (!parentNode) {
            console.error("Parent node not found for id:", id);
        } else {
            let newChildNode = {
                id: newNodeId,
                position: { x: positionAbsoluteX, y: positionAbsoluteY + (parentNode.type === 'router' ? 60 : newType === 'path' ? 60 : 35) },
                data: {
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: parentNode.type === 'router' ? 'childpath' : 'child',
                sourcePosition: 'right',
                targetPosition: 'left',
                // deletable: false
            };
            if (parentNode.type === 'router') {
                newChildNode.data["restfulapi_parent_node"] = parentNode.data.restfulapi_parent_node
                newChildNode.data["router_parent_node"] = parentNode.id
            } else {
                newChildNode.data["restfulapi_parent_node"] = parentNode.id
            }
            addNodes(newChildNode);
            const newEdge = {
                id: `${parentNode.id}->${newNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: process.env.REACT_APP_EDGES_COLOR
                },
                source: parentNode.id,
                target: newNodeId,
            };
            addEdges(newEdge);
        }

    }, [setNodes]);

    const findParentNode = (edges, targetNodeId) => {
        const findTargetEdge = edges.find(edge => edge.target === targetNodeId);
        if (!findTargetEdge) return null;
        const parentNode = getNode(findTargetEdge.source);
        return parentNode;
    };

    const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(
        nodes,
        edges,
        { treeWidth, treeHeight }
    );
    const { nodes: animatedNodes } = useAnimatedNodes(visibleNodes, {
        animationDuration,
    });

    const onNodesChange = useCallback(
        async (changes) => {
            if (openSimulatorPolicy) {
                return;
            }

            setNodes((nodes) => {
                const oldNodes = nodes.map(
                    (node) => ({
                        ...node,
                        measured: {
                            width: node?.measured?.width || 150,
                            height: node?.measured?.height || 40,
                        }
                    })
                );
                return applyNodeChanges(changes, oldNodes);
            });
        },
        [openSimulatorPolicy, setNodes],
    );

    const onEdgesChange = useCallback(
        (changes) => {
            if (openSimulatorPolicy) {
                return;
            }

            setEdges((eds) => {
                const updatedEdges = applyEdgeChanges(changes, eds);
                return updatedEdges.map((edge) => applyEdgeSelectionStyle(edge, edge.selected, edge.id === hoveredEdgeId));
            });
        },
        [hoveredEdgeId, openSimulatorPolicy, setEdges],
    );

    // Memoized: Get list of clients (fetch only once)
    const getListClients = useCallback(async () => {
        try {
            const response = await fetch(`https://${process.env.REACT_APP_OP_HOST}/iap-api/get-clients?app_type=WEB,NATIVE,API`);
            const data = await response.json();
            dispatch(setClients(data));
            return data;
        } catch (error) {
            console.error('❌ Error fetching clients:', error);
            return [];
        }
    }, [dispatch]);

    // Memoized: Get DRG list for specific project
    const getDRGList = useCallback(async () => {

        if (!project?.project_id) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/regulatory-diagram/get-regulatories/${project.project_id}`);
            const data = await response.json();
            const newData = data.map((item) => ({
                id: item.regulatory_diagram_id,
                name: item.regulatory_diagram_name,
                validation: item.validation,
                action: item?.regulatory_diagram_action || null
            }));
            dispatch(setDRGFiles(newData));
        } catch (error) {
            console.error('❌ Error fetching DRG list:', error);
        }
    }, [dispatch, project?.project_id]);

    const filterClientsInvalid = (nodes, clients) => {
        const usedClients = findUsedClients(nodes);
        const invalidClients = usedClients.filter(used => {
            return !clients.some(client => client.client_name === used.client_name && client.app_type === used.app_type);
        });
        // console.log('invalidClients', invalidClients);
        setClientsInvalid(invalidClients);
        if (invalidClients.length > 0) {
            setOpenNotifyDialog(true);
        }
    };

    const checkSubjectConstraintValidation = (nodes, currentSubjectSchema = subjectSchema) => {
        const resolvedSubjectSchema = Array.isArray(currentSubjectSchema) ? currentSubjectSchema : [];

        const validationNodes = nodes.map(node => {
            if (node.data?.general_properties?.subjectConstraints) {
                let new_error_text = node?.data?.error_text.filter(err => (err !== 'Invalid constraint.' && err !== 'Constraint invalid. Please correct it.'));

                if (node.data?.general_properties?.subjectConstraints.length > 0) {
                    const checkSubjectConstraintError = node.data.general_properties.subjectConstraints.map(constraint => {
                        const checkSubConstraintError = constraint.child.map(sub => {
                            const { attr_error_text, data_type_error_text, operator_error_text, ...sub_without_error } = sub;
                            const hasAttribute = resolvedSubjectSchema.some(attr => attr.name === sub.l_operand.name);
                            if (!hasAttribute) {
                                return {
                                    ...sub_without_error,
                                    attr_error_text: "Attribute no longer available. Please select another."
                                }
                            } else {
                                // ตรวจสอบ attribute type ของ attribute ที่เลือกใน suject constraint กับ subject schema ใน authorized data ว่าตรงกันหรือไม่
                                const newDataType = resolvedSubjectSchema.find(attr => attr.name === sub.l_operand.name)?.attributeType;
                                // const new_group_type = dataTypes.find(dt => dt.similar_type.includes(newDataType));
                                // const new_operators = new_group_type ? new_group_type.operators : [];

                                let subjectAttr = {
                                    ...sub_without_error,
                                    l_operand: {
                                        ...sub_without_error.l_operand,
                                        // attributeType: newDataType,
                                        // operators: new_operators,
                                        // type: new_group_type ? new_group_type.type : undefined
                                    }
                                }

                                if (sub.l_operand.type !== newDataType) {
                                    subjectAttr = {
                                        ...subjectAttr,
                                        attr_error_text: `Attribute type changed. Expected ${newDataType}.`
                                    }
                                }

                                // const typeValidator = createLDAPValidator(newDataType);

                                // const normalizedNewDataType = String(newDataType).toLowerCase();
                                // const normalizedOperator = String(sub_without_error.operator).toLowerCase();
                                // const isArrayIncludesOperator = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                                //     && (normalizedOperator === 'includes' || normalizedOperator.includes('includes'));
                                // const isArrayTypeWithStringValue = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                                //     && !Array.isArray(sub_without_error.r_operand)
                                //     && sub_without_error.r_operand !== '';

                                // // Check for IN/NOT IN operators
                                // const isInNotInOp = isInOperator(sub_without_error.operator);
                                // const isArrayValue = Array.isArray(sub_without_error.r_operand);

                                // let validateResult;

                                // if (isArrayIncludesOperator && isArrayTypeWithStringValue) {
                                //     // For array type with array value, accept each element without strict array validation
                                //     validateResult = {
                                //         valid: true,
                                //         parsed: sub_without_error.r_operand,
                                //         error: null
                                //     };
                                // } else if (isInNotInOp && isArrayValue) {
                                //     // For IN/NOT IN operators with array value, accept without strict validation
                                //     validateResult = {
                                //         valid: true,
                                //         parsed: sub_without_error.r_operand,
                                //         error: null
                                //     };
                                // } else {
                                //     validateResult = typeValidator.validate(sub_without_error.r_operand);
                                // }

                                // const isOperatorMismatch = !isInNotInOp && !isArrayIncludesOperator && !isOperatorSupported(subjectAttr.l_operand.operators, sub.operator);

                                // const isOperatorMismatch = !isInNotInOp && !isArrayIncludesOperator && !isOperatorSupported(new_operators, sub.operator);

                                // if (isOperatorMismatch) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         operator_error_text: "Operator for attribute type invalid."
                                //     }
                                // }

                                // if (!validateResult.valid && (isInNotInOp || isArrayIncludesOperator)) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         r_operand: sub_without_error.r_operand
                                //     }
                                // } else if (!validateResult.valid) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         data_type_error_text: validateResult.error
                                //     }
                                // } else {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         r_operand: validateResult.parsed
                                //     }
                                // }

                                return subjectAttr;
                            }
                        });

                        const hasSubError = checkSubConstraintError.some(sub => sub.attr_error_text || sub.data_type_error_text || sub.operator_error_text);
                        if (hasSubError) {
                            return {
                                ...constraint,
                                child: checkSubConstraintError,
                                error: true,
                            }
                        } else {
                            const { error, ...constraint_without_error } = constraint;
                            return {
                                ...constraint_without_error,
                                child: checkSubConstraintError
                            };
                        }

                    });

                    const hasError = checkSubjectConstraintError.some(constraint => constraint.error === true);

                    if (hasError) {
                        new_error_text.push("Constraint invalid. Please correct it.");
                    }

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: checkSubjectConstraintError
                            },
                            error: new_error_text.length > 0,
                            error_text: new_error_text
                        }
                    }

                }

                return {
                    ...node,
                    data: {
                        ...node.data,
                        error: new_error_text.length > 0,
                        error_text: new_error_text
                    }
                };
            }

            return node;

        });

        return validationNodes;
    };

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            if (openSimulatorPolicy) {
                return;
            }

            const type = event.dataTransfer.getData('application/reactflow');

            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            let initialChildNode = false;

            let newNode = {
                id: uuidv4(),
                type: type,
                position,
                data: {
                    general_properties: {
                        name: null,
                        description: '',
                        subjectConstraints: [],
                        action: '',
                    },
                    environment_properties: {
                        resources: [],
                        ip_address_verification: {
                            ipv4: {
                                wild_card: [],
                                subnet_cidr: [],
                                range: [],
                                active: false
                            }
                        },
                        date_time_verification: {
                            active: false,
                            date_time: []
                        }
                    },
                    error_text: ["General properties incomplete. Please complete them."],
                },
            };

            if (type === 'application') {
                newNode.data.general_properties.action = 'OPEN';
            }

            if (type === 'restfulapi') {
                newNode.data.general_properties.action = ['GET', 'POST', 'PUT', 'DELETE'];
                newNode.data.expanded = false;
                newNode.data.expandable = true;
                newNode.data.general_properties['prefix_path'] = '';
                initialChildNode = true;
            }

            if (type === 'data') {
                // const { name, action, ...currentProperties } = newNode.data.general_properties;
                const { name, ...currentProperties } = newNode.data.general_properties;
                newNode = {
                    ...newNode,
                    data: {
                        ...newNode.data,
                        general_properties: {
                            ...currentProperties,
                            drg: null
                        },
                        // error_text: ["General properties incomplete. Please complete them.", "No RESTful API node connection. Please connect this node to a RESTful API node."]
                        error_text: ["General properties incomplete. Please complete them."]
                    }
                }
            }

            setNodes((nds) => {
                const nextNodes = nds.concat(newNode);

                // Keep ref in sync immediately because state/store updates are async.
                nodesRef.current = nextNodes;
                return nextNodes;
            });

            if (initialChildNode) {
                const newChildNodeId = uuidv4();

                const positionAbsoluteX = newNode.position.x;
                const positionAbsoluteY = newNode.position.y;

                addNodes({
                    id: newChildNodeId,
                    position: { x: positionAbsoluteX + 220, y: positionAbsoluteY + 12 },
                    data: {
                        restfulapi_parent_node: newNode.id
                    },
                    type: 'child',
                    sourcePosition: 'right',
                    targetPosition: 'left',
                });

                const newEdge = {
                    id: `${newNode.id}->${newChildNodeId}`,
                    style: edgeStyle,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: process.env.REACT_APP_EDGES_COLOR
                    },
                    source: newNode.id,
                    target: newChildNodeId,
                };

                addEdges(newEdge);
            }

        },
        [openSimulatorPolicy, screenToFlowPosition, setNodes]
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        if (openSimulatorPolicy) {
            event.dataTransfer.dropEffect = 'none';
            return;
        }
        event.dataTransfer.dropEffect = 'move';
    }, [openSimulatorPolicy]);

    const saveViewportState = useCallback(() => {
        const viewport = getViewport();
        // const all_nodes = getNodes().map(node => ({
        //     id: node.id,
        //     position: node.position,
        //     data: node.data,
        //     type: node.type,
        //     sourcePosition: node.sourcePosition,
        //     targetPosition: node.targetPosition,
        //     measured: node.measured
        // }));
        // const all_edges = getEdges().map(edge => ({
        //     id: edge.id,
        //     source: edge.source,
        //     target: edge.target,
        //     style: edge.style,
        //     markerEnd: edge.markerEnd
        // }));
        const viewport_flow = {
            // nodes: all_nodes,
            // edges: all_edges,
            viewport: viewport
        }
        let allFlows = JSON.parse(localStorage.getItem('policy-viewport')) || {};
        const currentFile = JSON.parse(localStorage.getItem('policy-file')) || {};
        allFlows[currentFile.policy_diagram_id] = viewport_flow;
        localStorage.setItem('policy-viewport', JSON.stringify(allFlows));
    }, [getViewport, getNodes, getEdges]);

    // const onNodeDragStart = useCallback((e, node, nodes) => {
    //     // console.log("onNodeDragStart: ", node)
    //     // setNodes((prev) =>
    //     //     prev.map((nd) => ({ ...nd, data: { ...nd.data, showHandle: false } }))
    //     // )
    //     // dispatch(setSelectedNodeRedux(node));
    //     // setFirstCreate(false);
    //     // setOpenProperties(true);
    // }, []);

    const onNodeDoubleClick = useCallback(() => {
        // console.log("onNodeDoubleClick");
        if (openSimulatorPolicy) {
            return;
        }
        dispatch(setOpenDrawerRight(true));
    }, [dispatch, openSimulatorPolicy]);

    const onNodeClick = (event, nodeId) => {
        if (openSimulatorPolicy) {
            event.stopPropagation();
            return;
        }

        const node = getNode(nodeId.id);
        // console.log("click node: ", node);

        event.stopPropagation(); // ป้องกัน event ซ้อนกัน
        if (!node) return;

        dispatch(setSelectedNodeRedux(node));

        // setNodes((prev) =>
        //     prev.map((nd) => ({
        //         ...nd,
        //         data: {
        //             ...nd.data,
        //             showHandle: (nd.id === nodeId.id) ? true : false,
        //         },
        //     }))
        // )

        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === node.id) {
                    return {
                        ...n,
                        data: {
                            ...n.data,
                            expanded: !n?.data?.expanded,
                            showHandle: true
                        },
                    };
                }

                return {
                    ...n,
                    data: {
                        ...n.data,
                        showHandle: false
                    }
                };
            })
        );

    };

    const onPaneClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (openSimulatorPolicy) {
            return;
        }
        setNodes((prev) =>
            prev.map((node) => ({ ...node, data: { ...node.data, showHandle: false } }))
        )
        setEdges((prev) => prev.map((edge) => applyEdgeSelectionStyle(edge, false)));
        dispatch(clearSelectedNodeRedux());
    }, [dispatch, setEdges, setNodes, openSimulatorPolicy]);

    const onEdgeClick = useCallback((event, edge) => {
        if (openSimulatorPolicy) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.id === edge.id)));
    }, [openSimulatorPolicy, setEdges]);

    const onEdgeMouseEnter = useCallback((event, edge) => {
        if (openSimulatorPolicy) {
            return;
        }
        setHoveredEdgeId(edge.id);
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.selected, item.id === edge.id)));
    }, [openSimulatorPolicy, setEdges]);

    const onEdgeMouseLeave = useCallback(() => {
        if (openSimulatorPolicy) {
            return;
        }
        setHoveredEdgeId(null);
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.selected, false)));
    }, [openSimulatorPolicy, setEdges]);

    useEffect(() => {
        if (!nodes || !selectedNode) return;

        const targetNode = nodes.find(node => node.id === selectedNode.id);
        if (!targetNode) return;

        // เปรียบเทียบเฉพาะ position และ data ที่สำคัญ
        const positionChanged =
            targetNode.position?.x !== selectedNode.position?.x ||
            targetNode.position?.y !== selectedNode.position?.y;

        // เปรียบเทียบ data ด้วย JSON string (ใช้เมื่อจำเป็นจริง ๆ)
        let dataChanged = false;
        try {
            dataChanged = JSON.stringify(targetNode.data) !== JSON.stringify(selectedNode.data);
        } catch (e) {
            // ถ้า data มี circular reference ให้ข้ามการเปรียบเทียบ
            dataChanged = false;
        }

        if (positionChanged || dataChanged) {
            const updated = {
                id: targetNode.id,
                position: { ...targetNode.position },
                data: { ...targetNode.data },
                type: targetNode.type
            };
            dispatch(setSelectedNodeRedux(updated));
        }

    }, [nodes, dispatch]);

    const shouldShowHandle = (start_node, node) => {
        if (start_node.type === 'path' && node.type === 'data') {
            return true;
        } else if (start_node.type === 'application' && node.type === 'restfulapi') {
            return true;
        } else if (start_node.type === 'restfulapi' && node.type === 'application') {
            return true;
        } else {
            return false;
        }
    };

    const onConnectStart = (event, params) => {
        if (openSimulatorPolicy) {
            return;
        }
        // console.log("onConnectStart: ", params);
        const start_node = getNode(params.nodeId);
        setNodes(nodes => nodes.map((node) => ({
            ...node,
            data: { ...node.data, showHandle: shouldShowHandle(start_node, node) }
        })));
    };

    const hasConnectedSourceEdge = (nodeId, edges) => {
        return edges.some(
            (edge) => edge.source === nodeId
        );
    };

    const hasConnectedTargetEdge = (nodeId, edges) => {
        return edges.some(
            (edge) => edge.target === nodeId
        );
    };

    const checkActionMismatch = (sourceAction, targetAction) => {
        if ((sourceAction === 'GET' || sourceAction === 'POST') && targetAction === 'SELECT') {
            return false;
        } else if ((sourceAction === 'POST') && targetAction === 'INSERT') {
            return false;
        } else if ((sourceAction === 'PUT') && targetAction === 'UPDATE') {
            return false;
        } else if ((sourceAction === 'DELETE') && targetAction === 'DELETE') {
            return false;
        } else {
            return true;
        }
    }

    const onConnect = useCallback(
        async (params) => {
            if (openSimulatorPolicy) {
                return;
            }

            if (params.source === params.target) { // ไม่ให้เชื่อมเข้าตัวมันเอง
                return;
            }
            const isDuplicate = edges.some(
                (edge) => edge.source === params.source && edge.target === params.target
            );
            if (isDuplicate) { // เช็กเส้นซ้ำ
                return;
            }

            const sourceNode = getNode(params.source);
            const targetNode = getNode(params.target);

            if (!sourceNode || !targetNode) {
                return;
            }

            if (sourceNode.type === targetNode.type) {
                return;
            }

            if (sourceNode.type !== 'path' && targetNode.type === 'data') {
                return;
            } else { // sourceNode === 'path'
                if (!sourceNode?.data?.general_properties?.action && sourceNode.type === 'path') {
                    return; // แจ้งเตือนไม่สามารถเชื่อมต่อได้ เนื่องจาก path ไม่มีการเลือก action
                }
            }

            if (sourceNode.type === 'application' && targetNode.type === 'restfulapi') { // Application node ที่ไม่สมบูรณ์ ไม่สามารถเชื่อมต่อกับ Restfulapi node ได้
                if (sourceNode?.data?.error_text.length > 0) {
                    return;
                }
            }

            if (sourceNode.type === 'path' && targetNode.type !== 'data') { // Path node จะเชื่อมต่อได้แค่ Data node
                return;
            } else if (sourceNode.type === 'path' && targetNode.type === 'data') { // เช็กว่า path node มีการเชื่อมต่อกับ data node อื่นอยู่หรือไม่
                const existingSourceEdge = hasConnectedSourceEdge(sourceNode.id, edges);
                const existingTargetEdge = hasConnectedTargetEdge(targetNode.id, edges);
                if (existingSourceEdge || existingTargetEdge) {
                    return;
                }
                const sourceAction = sourceNode?.data?.general_properties?.action;
                const dataAction = targetNode?.data?.general_properties?.action;
                // const convertDataAction = convertActionPathToActionData(sourceAction);
                const isActionMismatch = checkActionMismatch(sourceAction, dataAction);
                if (isActionMismatch) {
                    return dispatch(setSnackbarMessage({
                        open: true,
                        message: `Error: Action mismatch. Please correct the action in general properties.`,
                        status: "error",
                    }));
                }
            }

            if (sourceNode.type === 'application' && targetNode.type !== 'restfulapi') { // Application node จะเชื่อมต่อได้แค่ Restfulapi node
                return;
            }

            const edgeStyle = { stroke: process.env.REACT_APP_EDGES_COLOR, strokeWidth: 1 };

            const newEdge = {
                ...params,
                id: `${sourceNode.id}-${targetNode.id}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: process.env.REACT_APP_EDGES_COLOR
                }
            };

            const newEdges = addEdge(applyEdgeSelectionStyle(newEdge, false), edges);

            setEdges(newEdges);

            handleValidateInherited(sourceNode, targetNode);

        },
        [openSimulatorPolicy, setEdges, edges, getNode],
    );

    const convertActionPathToActionData = (action) => {
        let data_action = '';
        if (action === 'GET') {
            data_action = 'SELECT';
        } else if (action === 'POST') {
            data_action = 'INSERT';
        } else if (action === 'PUT') {
            data_action = 'UPDATE';
        } else {
            data_action = 'DELETE';
        }
        return data_action;
    };

    const updateEnvironmentToChilds = (newEnvProp, targetNode) => {
        if (targetNode?.type === 'restfulapi') {
            const nodes_update = getListChildsUpdateEnvironment(targetNode);
            setNodes((prev) => prev.map((node) => {
                if (nodes_update.some(n => n === node.id)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            environment_properties: newEnvProp
                        }
                    }
                }
                return node;
            }))
        }
    };

    const getListChildsUpdateEnvironment = (targetNode) => {
        let list_nodes = [];
        const traverseChilds = (nodeId) => {
            const childs = findChildrenNode(nodesRef.current, edgesRef.current, nodeId);
            for (const nodeId of childs) {
                let node = getNode(nodeId);
                if (node?.type === 'router' || node?.type === 'path') {
                    list_nodes.push(node.id)
                    traverseChilds(node.id)
                } else {
                    continue;
                }
            }
        };
        if (targetNode?.id) {
            traverseChilds(targetNode.id)
        }
        return list_nodes;
    };

    const handleValidateInherited = (sourceNode, targetNode) => {
        if (sourceNode.type !== targetNode.type) {
            if (sourceNode.type === 'application' && targetNode.type === 'restfulapi') {
                const newResources = {
                    ...targetNode.data.environment_properties,
                    resources: [
                        ...targetNode.data.environment_properties.resources,
                        {
                            id: sourceNode.id,
                            name: sourceNode.data.general_properties.name
                        }
                    ]
                }
                setNodes((prev) =>
                    prev.map((node) => {
                        if (node.id === targetNode.id) {
                            return {
                                ...targetNode,
                                data: {
                                    ...targetNode.data,
                                    environment_properties: newResources
                                }
                            }
                        }
                        return node;
                    })
                );
                updateEnvironmentToChilds(newResources, targetNode);
            } else if (sourceNode.type === 'path' && targetNode.type === 'data') {
                const data_action = convertActionPathToActionData(sourceNode?.data?.general_properties?.action);
                const api_parent_node = getNode(sourceNode?.data?.restfulapi_parent_node)
                let newTargetNode = {
                    ...targetNode,
                    data: {
                        ...targetNode.data,
                        connect_with_path: sourceNode.id,
                        general_properties: {
                            ...targetNode.data.general_properties,
                            action: data_action
                        },
                        environment_properties: {
                            ...targetNode.data.environment_properties,
                            resources: [
                                ...targetNode.data.environment_properties.resources,
                                {
                                    id: api_parent_node.id,
                                    name: api_parent_node.data.general_properties.name,
                                }
                            ]
                        }
                    }
                };
                const clear_err = newTargetNode.data.error_text.filter(err => (
                    err !== 'Cannot proceed: no connection to a path node.'
                    && err !== 'No RESTful API node connection. Please connect this node to a RESTful API node.'
                ));
                newTargetNode.data.error_text = clear_err;
                setNodes((prev) =>
                    prev.map((node) => (node.id === targetNode.id ? newTargetNode : node))
                );
            }
        }
    };

    const handleConfirmRemove = () => {
        const { edges: deletedEdges, nodes: deletedNodes } = flowDelete;

        let newEdges = edgesRef.current;
        let newNodes = nodesRef.current;

        let deleted_edges = [...deletedEdges];

        if (deletedEdges.length > 0) {
            deleted_edges = handleFilterEdgeinFlow(deleted_edges, newNodes);
            newNodes = handleClearResources(deleted_edges, newNodes);
            newEdges = edgesRef?.current.filter(edge => !deleted_edges.some(deletedEdge => deletedEdge.id === edge.id));
        }

        if (deletedNodes.length > 0) {
            const node_deleted = validateRemoveNode(deletedNodes, nodesRef.current)
            newNodes = newNodes.filter(node => !node_deleted.some(item => node.id === item.id));
            dispatch(clearSelectedNodeRedux());
        }

        newEdges = handleClearEdgesNodes(newEdges, newNodes);

        setEdges(newEdges);
        setNodes(newNodes);

        setOpenConfirmRemove(false)
    };

    const handleClearEdgesNodes = (edges, nodes) => {
        let newEdges = [...edges];
        for (const edge of newEdges) {
            const sourceNode = nodes.find((node) => node.id === edge.source);
            const targetNode = nodes.find((node) => node.id === edge.target);
            if (!sourceNode || !targetNode) {
                newEdges = newEdges.filter(e => e.id !== edge.id); // ลบ edge ที่ไม่พบ node ที่เชื่อมต่อ
            } else {
                continue;
            }
        }
        return newEdges;
    };

    const handleFilterEdgeinFlow = (deletedEdges, nodes) => {
        let newDeletedEdges = [...deletedEdges];
        for (const edge of deletedEdges) {
            const sourceNode = nodes.find((node) => node.id === edge.source);
            const targetNode = nodes.find((node) => node.id === edge.target);

            if (sourceNode && (
                targetNode?.type === 'router'
                || targetNode?.type === 'path'
                || targetNode?.type === 'child'
                || targetNode?.type === 'childpath'
            )) {
                newDeletedEdges = newDeletedEdges.filter(e => e.id !== edge.id); // ลบ edge ที่ตรงกับ deletedEdges
            } else {
                continue; // ถ้าไม่พบ node ที่เชื่อมต่อกับ edge นั้น ๆ ให้ข้ามไป
            }

        }
        return newDeletedEdges;
    }

    const validateRemoveNode = (deletedNodes, nodes) => {
        let del_nodes = [...deletedNodes];
        for (const node of deletedNodes) {
            if (node.type === 'child') {
                const hasRestfulApiParent = deletedNodes.some(n => n.id === node.data.restfulapi_parent_node);
                if (!hasRestfulApiParent) {
                    del_nodes = del_nodes.filter(n => n.id !== node.id); // ไม่ลบ child node ที่เป็น child ของ router หรือ path
                }
            } else if (node.type === 'childpath') {
                const hasRouterParent = deletedNodes.some(n => n.id === node.data.router_parent_node);
                if (!hasRouterParent) {
                    del_nodes = del_nodes.filter(n => n.id !== node.id); // ไม่ลบ child path node ที่เป็น child ของ router หรือ path
                }
            } else if (node.type === 'router') {
                const deletable_nodes = filterRouterChildNodes(node, nodes);
                del_nodes = del_nodes.concat(deletable_nodes)
            } else if (node.type === 'restfulapi') {
                const deletable_nodes = filterApiChildNodes(node, nodes);
                del_nodes = del_nodes.concat(deletable_nodes)
            }
        }
        return del_nodes;
    };

    const filterApiChildNodes = (targetNode, nodes) => {
        const filterNode = nodes.filter(n => n?.data?.restfulapi_parent_node === targetNode.id);
        return filterNode;
    };

    const filterRouterChildNodes = (targetNode, nodes) => {
        const filterNode = nodes.filter(n => n?.data?.router_parent_node === targetNode.id);
        return filterNode;
    };

    const onDelete = async (deletedData) => {
        if (openSimulatorPolicy) {
            return;
        }
        // console.log("Deleted data:", deletedData);
        const { edges: deletedEdges, nodes: deletedNodes } = deletedData;

        let newEdges = edgesRef.current;
        let newNodes = nodesRef.current;

        // setFlowDelete({
        //     nodes: deletedNodes,
        //     edges: deletedEdges
        // })

        // if (deletedEdges.length > 0) {
        //     setOpenConfirmRemove(true);
        // }

        // if (deletedNodes.length > 0) {
        //     setOpenConfirmRemove(true);
        // }

        // setEdges(newEdges);
        // setNodes(newNodes);

        let deleted_edges = [...deletedEdges];

        if (deletedEdges.length > 0) {
            deleted_edges = handleFilterEdgeinFlow(deleted_edges, newNodes);
            newNodes = handleClearResources(deleted_edges, newNodes);
            newEdges = edgesRef?.current.filter(edge => !deleted_edges.some(deletedEdge => deletedEdge.id === edge.id));
        }

        if (deletedNodes.length > 0) {
            const node_deleted = validateRemoveNode(deletedNodes, nodesRef.current)
            newNodes = newNodes.filter(node => !node_deleted.some(item => node.id === item.id));
            dispatch(clearSelectedNodeRedux());
        }

        newEdges = handleClearEdgesNodes(newEdges, newNodes);

        setEdges(newEdges);
        setNodes(newNodes);

    };

    const handleClearResources = (deletedEdges, allNodes) => {
        let all_nodes = allNodes;
        for (const edge of deletedEdges) {
            const sourceNode = all_nodes.find((node) => node.id === edge.source);
            const targetNode = all_nodes.find((node) => node.id === edge.target);

            if (sourceNode?.type === 'application' && targetNode?.type === 'restfulapi') {
                all_nodes = all_nodes.map((node) => {
                    if (node.id === targetNode.id) {
                        return {
                            ...targetNode,
                            data: {
                                ...targetNode?.data,
                                environment_properties: {
                                    ...targetNode?.data.environment_properties,
                                    resources: targetNode?.data?.environment_properties?.resources.filter(
                                        (resource) => resource.id !== sourceNode.id
                                    )
                                }
                            }
                        };
                    } else if (node.type === 'router' || node.type === 'path' || node.type === 'child' || node.type === 'childpath') {
                        const restfulapi_parent_node = node?.data?.restfulapi_parent_node;
                        if (restfulapi_parent_node === targetNode.id) {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    environment_properties: {
                                        ...node.data.environment_properties,
                                        resources: node?.data?.environment_properties?.resources.filter(
                                            (resource) => resource.id !== sourceNode.id
                                        )
                                    }
                                }
                            }
                        }
                    }
                    return node;
                });

            } else if (targetNode?.type === 'data') {
                let newTargetNode = {
                    ...targetNode,
                    data: {
                        ...targetNode?.data,
                        connect_with_path: '',
                        general_properties: {
                            ...targetNode?.data.general_properties,
                            action: targetNode?.data.general_properties?.action || '',
                        },
                        environment_properties: {
                            ...targetNode?.data.environment_properties,
                            resources: []
                        }
                    }
                };
                // const checkErrorText = newTargetNode.data.error_text.includes(('Cannot proceed: no connection to a path node.' || 'No RESTful API node connection. Please connect this node to a RESTful API node.'));
                // if (!checkErrorText) {
                //     const updatedError = [...newTargetNode.data.error_text];
                //     updatedError.push('No RESTful API node connection. Please connect this node to a RESTful API node.');
                //     newTargetNode.data.error_text = updatedError;
                // }
                const error_text = newTargetNode.data.error_text.filter(err => (err !== 'Cannot proceed: no connection to a path node.' && err !== 'No RESTful API node connection. Please connect this node to a RESTful API node.'));
                newTargetNode.data.error_text = error_text;
                all_nodes = all_nodes.map((node) => (node.id === targetNode.id ? newTargetNode : node));
            }
        };
        return all_nodes;
    };

    const handleSave = async () => {
        localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'pending');
        let keepPendingForRefresh = false;

        try {
            const { id, ...oldPolicyFile } = policyFile;
            const now = Date.now();
            let update = {
                ...oldPolicyFile,
                policy_diagram_id: policyFile.policy_diagram_id,
                policy_diagram_name: policyFile.policy_diagram_name,
                creator_id: policyFile.creator_id,
                created_by: policyFile.created_by,
                updated_id: profile.inum,
                updated_by: profile.displayName,
                created_at: policyFile.created_at,
                last_updated: now,
                description: policyFile.description,
                status: "Unverified", // follow diagram updated
                validation: policyFile?.validation || [],
                diagram: {
                    nodes: nodesRef.current,
                    edges: edgesRef.current
                }
            }

            const used_drg_in_diagram = findUsedDRGInPolicy(nodesRef.current);
            if (used_drg_in_diagram.length > 0) {
                update['drg_id'] = used_drg_in_diagram;
            } else {
                update['drg_id'] = [];
            }

            update['used_subject_attributes'] = findUsedSubjectAttributesInPolicy(nodesRef.current);
            update = {
                ...update,
                used_clients: findUsedClients(update?.diagram?.nodes || [])
            }

            update = checkDiagramError(update);

            // console.log("Saving policy diagram:", update.diagram);

            const gzippedBody = gzip(JSON.stringify(update)); // บีบอัดข้อมูลด้วย gzip

            const requestOptions = {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    'Content-Encoding': 'gzip', // Inform the server the body is gzipped
                },
                body: gzippedBody
                // body: JSON.stringify(update)
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/policy-diagram/update-policy/${policyFile?.policy_diagram_id}`, requestOptions);
            if (response.status === 200) {
                keepPendingForRefresh = true;
                setIsUpdatePolicyFile(true);

                // validate policy diagram back-end
                try {
                    const requestPostOptions = {
                        method: "POST",
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(update)
                    };
                    const validate_response = await fetch(process.env.REACT_APP_API_VALIDATION_URL + `/verify/validate-policy?project_id=${project?.project_id}`, requestPostOptions);
                    if (validate_response.status === 200) {
                        // const result = await validate_response.json();
                        // console.log("Validation result:", result);
                    } else {
                        // console.error("Validation API error:", validate_response.status);
                        console.error(validate_response.status);
                        dispatch(setSnackbarMessage({
                            open: true,
                            message: `Error status: ${validate_response.status}`,
                            status: "error",
                        }));
                    }
                } catch (validateError) {
                    console.error(validateError);
                    dispatch(setSnackbarMessage({
                        open: true,
                        message: validateError?.message || "Validation API error",
                        status: "error",
                    }));
                }

                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Policy diagram updated successfully",
                    status: "success",
                }));

                return true;
            }

            console.error(response.status);
            dispatch(setSnackbarMessage({
                open: true,
                message: `Error status: ${response.status}`,
                status: "error",
            }));
            return false;
        } catch (error) {
            console.error(error);
            dispatch(setSnackbarMessage({
                open: true,
                message: error?.message || "Failed to save policy diagram",
                status: "error",
            }));
            return false;
        } finally {
            if (!keepPendingForRefresh) {
                localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'idle');
                window.dispatchEvent(new CustomEvent(POLICY_FILE_CHANGED_EVENT));
            }
        }
    };

    handleSaveRef.current = handleSave;

    useEffect(() => {
        const handleToolbarSaveRequest = (event) => {
            const diagramType = event?.detail?.diagramType;
            if (diagramType && diagramType !== DIAGRAM_TYPES.POLICY) {
                return;
            }

            if (openSimulatorPolicy) {
                return;
            }

            handleSaveRef.current?.();
        };

        window.addEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
        return () => window.removeEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
    }, [openSimulatorPolicy]);

    const handleExportPolicyStructure = async () => {
        const user_test = {
            displayName: "Bob",
            inum: "1234567890",
        }

        const policy_structure = await convertPoliciesStructure(project, [policyFile], user_test);
        // console.log("policy_structure", policy_structure)
    };

    useEffect(() => {

        const policyId = policyFile?.policy_diagram_id;

        const initializePolicyDiagram = async () => {
            // console.log("initializePolicyDiagram")
            if (!policyFile) {
                return;
            }

            let resolvedSubjectSchema = Array.isArray(subjectSchema) ? subjectSchema : [];

            if (resolvedSubjectSchema.length === 0) {
                const refreshedSubjectSchema = await checkUpdateSubjectSchema(dispatch, resolvedSubjectSchema, { returnData: true });
                if (Array.isArray(refreshedSubjectSchema)) {
                    resolvedSubjectSchema = refreshedSubjectSchema;
                }
            }

            const clientsData = await getListClients();

            if (clientsData.length === 0) return;

            const allFlows = JSON.parse(localStorage.getItem('policy-viewport')) || {};
            const currentFile = JSON.parse(localStorage.getItem('policy-file')) || {};
            const flowState = allFlows[currentFile?.policy_diagram_id];
            dispatch(setOpenDrawerRight(false));
            dispatch(setSelectedNodeRedux(undefined));
            const mapDrgValidation = checkDRGValidationFrontend(policyFile?.diagram.nodes, policyFile?.validation, clientsData);
            const mapSubjectConstraintValidation = checkSubjectConstraintValidation(mapDrgValidation, resolvedSubjectSchema);

            setNodes(mapSubjectConstraintValidation);
            setEdges(policyFile?.diagram.edges);

            // // Sync refs immediately because setState is async and other effects may read refs before next render.
            nodesRef.current = mapSubjectConstraintValidation;
            edgesRef.current = policyFile?.diagram.edges || [];

            setViewport(flowState?.viewport || { x: 0, y: 0, zoom: 1 });
            filterClientsInvalid(mapSubjectConstraintValidation, clientsData);
        };

        if (!policyId) return;

        // console.log("Policy diagram updated");
        initializePolicyDiagram();
        getDRGList();

    }, [policyFile?.policy_diagram_id, policyFile?.last_updated]);

    useEffect(() => {
        if (!openSimulatorPolicy) {
            hasHandledOpenSimulatorRef.current = false;
            return;
        }

        setNodes((prev) =>
            prev.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    showHandle: false,
                },
            }))
        );
        setEdges((prev) => prev.map((edge) => applyEdgeSelectionStyle(edge, false, false)));
        dispatch(clearSelectedNodeRedux());

        if (!policyFile) return;

        if (hasHandledOpenSimulatorRef.current) {
            return;
        }

        hasHandledOpenSimulatorRef.current = true;

        const latestNodes = nodesRef.current.length > 0 ? nodesRef.current : nodes;
        const latestEdges = edgesRef.current.length > 0 ? edgesRef.current : edges;

        // console.log("Check nodes before open Simulator:", latestNodes);

        if (latestNodes.length === 0) {
            dispatch(setOpenSimulatorPolicy(false));
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot open Simulator because the diagram is empty.",
                status: "error",
            }));
            return;
        }

        const updatedFile = {
            ...policyFile,
            diagram: {
                nodes: latestNodes,
                edges: latestEdges
            }
        }

        const result = checkDiagramError(updatedFile);
        if (result?.validation.length > 0) {
            dispatch(setOpenSimulatorPolicy(false));
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot open Simulator because the diagram has errors.",
                status: "error",
            }));
        } else {
            const openSimulatorWithLatestPolicy = async () => {
                const isSaved = await handleSave();
                if (!isSaved) {
                    dispatch(setOpenSimulatorPolicy(false));
                    return;
                }

                await handleGetSimulatorProfiles(project, profile, subjectSchema, dispatch);
            };

            void openSimulatorWithLatestPolicy();
        }
    }, [openSimulatorPolicy, policyFile, project, profile, subjectSchema, dispatch, setNodes, setEdges]);

    return (
        <div style={{ height: '100%' }}>
            <PolicyDiagramNodeTypeContext.Provider value={{ onTypeChange: handleTypeChange, isInteractionDisabled: openSimulatorPolicy }}>
                <ReactFlow
                    nodes={nodes}
                    onNodesChange={onNodesChange}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onMoveEnd={saveViewportState}
                    onNodeDoubleClick={onNodeDoubleClick}
                    onNodeClick={onNodeClick}
                    onPaneClick={onPaneClick}
                    onEdgeClick={onEdgeClick}
                    onEdgeMouseEnter={onEdgeMouseEnter}
                    onEdgeMouseLeave={onEdgeMouseLeave}
                    onConnect={onConnect}
                    onConnectStart={onConnectStart}
                    onDelete={onDelete}
                    nodesDraggable={!openSimulatorPolicy}
                    nodesConnectable={!openSimulatorPolicy}
                    elementsSelectable={!openSimulatorPolicy}
                    nodesFocusable={!openSimulatorPolicy}
                    edgesFocusable={!openSimulatorPolicy}
                    connectOnClick={!openSimulatorPolicy}
                    panOnDrag={true}
                    deleteKeyCode={openSimulatorPolicy ? null : ["Delete", "Backspace"]}
                // fitView
                >
                    <Background style={{ backgroundColor: "#fff" }} />
                </ReactFlow>
            </PolicyDiagramNodeTypeContext.Provider>
            <PolicyProperties setNodes={setNodes} nodes={nodes} edges={edges} policyFile={policyFile} setPolicyFile={setPolicyFile} />
            <ConfirmDialogReuse
                open={openConfirmRemove}
                maxWidth={"sm"}
                content={
                    <div>
                        <Typography variant="subtitle1">Are you sure you want to delete the selected nodes or edges?</Typography>
                    </div>
                }
                actions={
                    <div className='flex flex-row gap-2'>
                        <Button variant="outlined" onClick={() => handleConfirmRemove()}>OK</Button>
                        <Button variant="outlined" onClick={() => setOpenConfirmRemove(false)}>Cancel</Button>
                    </div>
                }
            />
            <DialogReuse
                open={openNotifyDialog}
                fullWidth={true}
                maxWidth={"sm"}
                title={"Invalid Clients Found"}
                content={
                    <div className='py-2 overflow-auto'>
                        {clientsInvalid.length > 0 &&
                            <Box>
                                <Typography component={'span'} variant="body1" gutterBottom>
                                    Some clients used in this policy are invalid or unavailable. Please review the related nodes and update their client properties.
                                    {/* {clientsInvalid.map((item, index) => {
                                        return (
                                            <div key={index} className='my-1'>
                                                <Typography component={'span'} variant="body2" color="error">- {item.client_name} ({item.app_type})</Typography>
                                            </div>
                                        )
                                    })} */}
                                </Typography>
                            </Box>
                        }
                    </div>
                }
                actions={
                    <LoadingButton variant="contained" onClick={() => setOpenNotifyDialog(false)}>OK</LoadingButton>
                }
            />
        </div>
    )
}

export default PolicyDiagram